import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, Typography } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { DataTable } from "@/components/tables";
import { TableHeaderType, TableRowItem } from "@/components/tables/types";
import { RelativeComponentLoader } from "@/components/loader";
import {
  getComputePoolDailyCost,
  getComputePoolEntries,
  getComputePoolOverview,
} from "@/helpers/apis";
import { ComputePoolEntry, ComputePoolOverview } from "./types";
import dayjs from "dayjs";
import { Chart, ChartTypes } from "@uicore";
import { Bar } from "recharts";
import { formatNumber } from "@uicore";
import { useAppState } from "@/modules/app/useAppContext";
import ComputePoolsTooltip from "./components/ComputePoolsTooltip";
import { DateRange } from "@/components/dateRange";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import FlexListSummary from "@/components/listSummary/FlexListSummary";
import { Paginate } from "@/components/paginate";
// duplicate import removed
import { formatCost } from "@/helpers/utils";
import { useBreadcrumb } from "@/components/breadcrumbs";

const OverviewCards = ({ overview }: { overview: ComputePoolOverview }) => {
  const m = overview.metadata;
  const { currency } = useAppState();
  const summary = [
    { label: "Total Spend", value: formatCost(Number(overview.total_cost || 0), currency) },
    { label: "Total Runs", value: String(overview.total_runs ?? 0) },
    { label: "Instance Family", value: m?.instance_family || "-" },
    { label: "Min/Max Nodes", value: `${m?.min_nodes ?? "-"}/${m?.max_nodes ?? "-"}` },
    { label: "Created", value: m?.created ? dayjs(m.created).format("YYYY-MM-DD") : "-" },
    { label: "Owner", value: m?.owner || "-" },
    { label: "Exclusive", value: m?.is_exclusive ? "Yes" : "No" },
  ];
  return <FlexListSummary summary={summary} />;
};

const EntriesTable = ({ items }: { items: ComputePoolEntry[] }) => {
  const { currency: currencyEntries } = useAppState();
  const formatDuration = (start?: string, end?: string | null) => {
    if (!start || !end) return "-";
    const s = dayjs(start);
    const e = dayjs(end);
    const minutes = Math.max(0, e.diff(s, "minute"));
    const hours = Math.floor(minutes / 60);
    const rem = minutes % 60;
    return hours > 0 ? `${hours}h ${rem}m` : `${rem}m`;
  };

  const header: TableHeaderType[] = [
    {
      id: 0,
      uid: "compute_pool_name",
      label: "Pool",
      render: (i: TableRowItem) => (i as ComputePoolEntry).compute_pool_name || "-",
    },
    {
      id: 1,
      uid: "start_time",
      label: "Start",
      render: (item: TableRowItem) =>
        dayjs((item as ComputePoolEntry).start_time).format("YYYY-MM-DD HH:mm"),
    },
    {
      id: 2,
      uid: "end_time",
      label: "End",
      render: (item: TableRowItem) =>
        ((it: ComputePoolEntry) =>
          it.end_time ? dayjs(it.end_time).format("YYYY-MM-DD HH:mm") : "-")(
          item as ComputePoolEntry
        ),
    },
    {
      id: 3,
      uid: "duration",
      label: "Duration",
      render: (i: TableRowItem) =>
        formatDuration(
          (i as ComputePoolEntry).start_time,
          (i as ComputePoolEntry).end_time as unknown as string
        ),
    },
    {
      id: 4,
      uid: "credits_used",
      label: "Cost",
      render: (i: TableRowItem) =>
        formatCost(Number((i as ComputePoolEntry).credits_used || 0), currencyEntries),
    },
    {
      id: 5,
      uid: "is_exclusive",
      label: "Exclusive",
      render: (i: TableRowItem) => ((i as ComputePoolEntry).is_exclusive ? "Yes" : "No"),
    },
    {
      id: 6,
      uid: "application_name",
      label: "Application",
      render: (i: TableRowItem) => (i as ComputePoolEntry).application_name || "-",
    },
    {
      id: 7,
      uid: "application_id",
      label: "App ID",
      render: (i: TableRowItem) => (i as ComputePoolEntry).application_id ?? "-",
    },
    {
      id: 8,
      uid: "instance_id",
      label: "Instance",
      render: (i: TableRowItem) => (i as ComputePoolEntry).instance_id || "-",
    },
  ];
  return <DataTable header={header} data={{ items }} />;
};

const ComputePoolDetail = () => {
  const { poolName = "" } = useParams();
  const decoded = decodeURIComponent(poolName);
  const { currency } = useAppState();

  useBreadcrumb(decoded || "Detail", {
    defaultParents: [{ label: "Compute Pools", pathname: "/compute-pools" }],
  });

  const { startDate, endDate, onUpdate } = useDateFromSearchParams();
  const { data: overview, isLoading: isOverviewLoading } = useQuery({
    queryKey: ["compute-pool-overview", decoded, startDate, endDate],
    queryFn: () =>
      getComputePoolOverview(decoded, {
        start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(endDate)!.toISOString(),
      }),
  });
  const { data: daily, isLoading: isDailyLoading } = useQuery({
    queryKey: ["compute-pool-daily-cost", decoded, startDate, endDate],
    queryFn: () =>
      getComputePoolDailyCost(decoded, {
        start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(endDate)!.toISOString(),
      }),
  });
  const { data: entriesPage, isLoading: isEntriesLoading } = useQuery({
    queryKey: ["compute-pool-entries", decoded, startDate, endDate],
    queryFn: () =>
      getComputePoolEntries(decoded, {
        start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(endDate)!.toISOString(),
      }),
  });

  if (isOverviewLoading || isDailyLoading || isEntriesLoading)
    return <RelativeComponentLoader label="Loading pool..." />;

  return (
    <div className="al-p-6 al-space-y-6">
      <Stack justify={"between"} align={"center"}>
        <Typography variant="page-heading">{decoded}</Typography>
        <DateRange
          startDate={startDate}
          endDate={endDate}
          availableStaticRanges={["Last 7 days", "Last 28 days"]}
          onDateRangeSelect={onUpdate}
        />
      </Stack>
      {overview ? <OverviewCards overview={overview} /> : null}
      <Card>
        <CardHeader>
          <Typography variant="section-heading">Daily Cost</Typography>
        </CardHeader>
        <CardContent className={"al-pb-10"}>
          <Chart
            title="Daily Cost"
            xAxisDataKey="date"
            xAxisLabelFormatter={value => dayjs(value).format("DD MMM")}
            yAxisLabelFormatter={value => formatNumber(Number(value), { currency })}
            type={ChartTypes.BarChart}
            data={(daily || []).map(d => ({
              date: d.date,
              cost: Number(d.cost ?? d.credits_used ?? 0),
            }))}
            tooltipProps={{
              content: props => <ComputePoolsTooltip {...props} />,
            }}
          >
            {() => (
              <Bar
                barSize={24}
                dataKey="cost"
                name="Cost"
                stackId="cost"
                radius={[5, 5, 0, 0]}
                fill="#247EFE"
              />
            )}
          </Chart>
        </CardContent>
      </Card>
      <Stack direction={"column"} className="p-3 al-bg-white al-rounded-md">
        <Stack direction={"row"} justify={"between"}>
          <h3 className={"al-text-xl al-font-medium"}>Entries</h3>
        </Stack>
        <EntriesTable items={entriesPage?.items || []} />
        <Paginate
          itemCount={entriesPage?.total || 0}
          numPages={entriesPage?.pages || 0}
          page={(entriesPage?.page || 1) - 1}
          pageSize={entriesPage?.size || 50}
          onPageClick={() => {}}
          onPageSizeChange={() => {}}
        />
      </Stack>
    </div>
  );
};

export default ComputePoolDetail;
